import { useEffect } from 'react';

interface SeoProps {
  path: string;
  title: string;
  description: string;
  noindex?: boolean;
  robotsDirective?: string;
  isNotFound?: boolean;
}

export const useSeo = ({ path, title, description, noindex = false, robotsDirective, isNotFound = false }: SeoProps) => {
  useEffect(() => {
    const is404 = isNotFound || path === '/404.html' || path === '/404';

    // 1. Update Title
    document.title = title;

    // 2. Helper to set or create meta tag
    const setMeta = (attrName: 'name' | 'property', attrValue: string, content: string) => {
      let meta = document.querySelector(`meta[${attrName}="${attrValue}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attrName, attrValue);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // 3. Meta Description
    setMeta('name', 'description', description);

    // 4. Open Graph & Twitter
    const fullCanonicalUrl = `https://82marketing.com${path.startsWith('/') ? path : `/${path}`}`;
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'twitter:title', title);
    setMeta('property', 'twitter:description', description);

    if (is404) {
      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) {
        ogUrl.remove();
      }
    } else {
      setMeta('property', 'og:url', fullCanonicalUrl);
    }

    // 5. Canonical Link
    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (is404) {
      if (canonical) {
        canonical.remove();
      }
    } else {
      let targetCanonical = canonical;
      if (!targetCanonical) {
        targetCanonical = document.createElement('link');
        targetCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(targetCanonical);
      }
      targetCanonical.setAttribute('href', fullCanonicalUrl);
    }

    // 6. Robots Meta (noindex)
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (noindex) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', robotsDirective || (is404 ? 'noindex' : 'noindex, nofollow'));
    } else {
      if (robots) {
        robots.remove();
      }
    }

    // 7. Hreflang links
    if (is404) {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    } else {
      const isHome = path === '/' || path === '';
      const pageUrl = `https://82marketing.com${path.startsWith('/') ? path : `/${path}`}`;

      // Helper to set or create link tag
      const setLink = (selector: string, rel: string, extraAttrs: Record<string, string>, href: string) => {
        let link = document.querySelector(selector) as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', rel);
          Object.entries(extraAttrs).forEach(([k, v]) => link!.setAttribute(k, v));
          document.head.appendChild(link);
        }
        link.setAttribute('href', href);
      };

      // hreflang en & x-default
      setLink('link[rel="alternate"][hreflang="en"]', 'alternate', { hreflang: 'en' }, pageUrl);
      setLink('link[rel="alternate"][hreflang="x-default"]', 'alternate', { hreflang: 'x-default' }, pageUrl);

      // hreflang ro alternate: ONLY exists on homepage ('https://82marketing.com/')
      const roHreflang = document.querySelector('link[rel="alternate"][hreflang="ro"]') as HTMLLinkElement | null;
      if (isHome) {
        setLink('link[rel="alternate"][hreflang="ro"]', 'alternate', { hreflang: 'ro' }, 'https://82marketing.ro/');
      } else {
        if (roHreflang) {
          roHreflang.remove();
        }
      }
    }
  }, [path, title, description, noindex, robotsDirective, isNotFound]);
};
